import {
  MainVisitReasons,
  SecondaryVisitReasons,
  SpecialtyCareOptions,
  alcoholUseOptions,
  drugUseOptions,
} from '../../clinical/reason-visit-questions';
import {
  AssessmentSections,
  MetaState,
  QuestionTypes,
  Screens,
} from '../types';

type MetaConfig = Partial<Record<Screens, Omit<MetaState, 'screen'>>>;

const introductionConfig: MetaConfig = {
  [Screens.LandingRouter]: {
    canGoBack: false,
    stepTitleIsNotPHI: true,
  },
  [Screens.Introduction]: {
    canGoBack: false,
    stepTitleIsNotPHI: true,
    subTitle: null,
    label: 'Let’s see if Talkiatry is right for you',
    subLabel: null,
    type: null,
    options: null,
    canRewrite: null,
    review: false,
    step: null,
    secondaryText: null,
    isTerminate: false,
    hasPreviouslyCompletedAssessment: null,
  },
  [Screens.IntroScreenOne]: {
    canGoBack: false,
    stepTitleIsNotPHI: true,
    label: "We're glad you're here",
    subLabel:
      'We’re a national, online psychiatry practice that takes insurance.',
    assessmentSection: AssessmentSections.Intro,
    isSectionIntro: true,
  },
  [Screens.IntroScreenTwo]: {
    canGoBack: true,
    stepTitleIsNotPHI: true,
    label: 'Psychiatrists are doctors for your mental health. They can:',
    assessmentSection: AssessmentSections.Intro,
  },
  [Screens.IntroScreenThree]: {
    canGoBack: true,
    stepTitleIsNotPHI: true,
    label: 'Wondering what we can treat?',
    subLabel:
      'Virtual psychiatry is safe and effective for many of the most common mental health conditions, including:',
    assessmentSection: AssessmentSections.Intro,
  },
  [Screens.IntroScreenFour]: {
    canGoBack: true,
    stepTitleIsNotPHI: true,
    label: 'How Talkiatry works',
    assessmentSection: AssessmentSections.Intro,
  },
  [Screens.GetStarted]: {
    label: 'Get started with our quick assessment',
    canGoBack: false,
    stepTitleIsNotPHI: true,
  },
  [Screens.ZocdocIntroductionFirst]: {
    canGoBack: false,
    stepTitleIsNotPHI: false,
    label:
      'Thanks for booking through Zocdoc. We have a few questions before your visit.',
  },
  [Screens.ZocdocIntroductionSecond]: {
    canGoBack: false,
    stepTitleIsNotPHI: false,
    label: "How we'll use this information",
  },
};

const authConfig: MetaConfig = {
  [Screens.CollectEmail]: {
    label: 'Let’s verify your email',
    subLabel:
      'We’ll send you an email. Open it and click the link to continue. We’ll use the address you give us to communicate with you about your care.',
    canGoBack: false,
    stepTitleIsNotPHI: true,
  },
  [Screens.ConfirmEmail]: {
    label: 'Check your email',
    canGoBack: false,
    stepTitleIsNotPHI: true,
  },
};

const demographicsConfig: MetaConfig = {
  [Screens.PreviouslyCompleted]: {
    label: 'This email is linked to an existing assessment',
    canGoBack: false,
    stepTitleIsNotPHI: false,
  },
  [Screens.PatientType]: {
    label: 'Who are you seeking care for?',
    canGoBack: false,
    stepTitleIsNotPHI: false,
    step: true,
  },
  [Screens.PersonalDetails]: {
    canGoBack: false,
    stepTitleIsNotPHI: false,
    step: true,
  },
  [Screens.StateCheck]: {
    canGoBack: true,
    stepTitleIsNotPHI: true,
    step: true,
  },
  [Screens.CollectDemographics]: {
    subTitle: 'Personal Details',
    step: true,
    canGoBack: true,
    stepTitleIsNotPHI: false,
    assessmentSection: AssessmentSections.Clinical, // Only true when intake 2.1 flag is on, will override if flag off
  },
  [Screens.ConsentReferrerSharing]: {
    label: 'We’d like to share some info with your referring clinician',
    step: true,
    canGoBack: true,
    stepTitleIsNotPHI: false,
  },
  [Screens.ConsentForMinor]: {
    label: 'Consent for minor',
    subLabel:
      'If you are providing consent to treatment as a parent, legal guardian, or other legal representative of the patient, please indicate your authority to act on behalf of the patient by checking the box below.',
    step: true,
    canGoBack: true,
    stepTitleIsNotPHI: false,
  },
  [Screens.OutOfCoverage]: {
    isTerminate: true,
    stepTitleIsNotPHI: false,
    canGoBack: false,
  },
  [Screens.CompletedByMinorTermination]: {
    isTerminate: true,
    stepTitleIsNotPHI: false,
    canGoBack: false,
  },
  [Screens.NotAMinorTermination]: {
    isTerminate: true,
    stepTitleIsNotPHI: false,
    canGoBack: false,
  },
  [Screens.GuardianDemographics]: {
    label: 'Primary guardian',
    subLabel:
      'This will be the primary point of contact for appointment reminders and other communications. You’ll be able to add a secondary guardian at the end of this form.',
    step: true,
    canGoBack: true,
    stepTitleIsNotPHI: false,
  },
};

const reasonVisitConfig: MetaConfig = {
  [Screens.RV_MainReason]: {
    step: true,
    canGoBack: true,
    label: 'Why are you seeking psychiatric care?',
    subLabel: 'These are the types of care we specialize in.',
    type: QuestionTypes.Many,
    options: MainVisitReasons,
    canRewrite: true,
    stepTitleIsNotPHI: true,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.RV_SecondaryReason]: {
    step: true,
    canGoBack: true,
    stepTitleIsNotPHI: true,
    label: 'Are you struggling with any of the following?',
    type: QuestionTypes.Many,
    options: SecondaryVisitReasons,
    canRewrite: true,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.RV_SpecialtyCare]: {
    step: true,
    canGoBack: true,
    stepTitleIsNotPHI: true,
    label: 'Do any of the following apply to you?',
    type: QuestionTypes.Many,
    options: SpecialtyCareOptions,
    canRewrite: true,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.SensitiveInformationWarning]: {
    label: 'Next, we’ll ask you a few questions about your mental health',
    canGoBack: true,
    stepTitleIsNotPHI: false,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.RV_PsychiatricImpatient]: {
    step: true,
    canGoBack: true,
    label:
      'In the past six months, were you required to stay overnight in a hospital or clinic due to a mental health concern?',
    type: QuestionTypes.One,
    options: ['Yes', 'No'],
    canRewrite: true,
    stepTitleIsNotPHI: true,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.NoService]: {
    isTerminate: true,
    stepTitleIsNotPHI: false,
    canGoBack: false,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.RV_KillingYourself]: {
    step: true,
    canGoBack: true,
    label: 'Do you feel like you are at risk of killing yourself?',
    type: QuestionTypes.One,
    options: ['Yes', 'No'],
    canRewrite: true,
    stepTitleIsNotPHI: true,
    secondaryText:
      'If you or someone you know is in crisis, call 911 or the National Suicide Prevention Hotline at 1-800-273-8255 right away.',
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.Emergency]: {
    isTerminate: true,
    stepTitleIsNotPHI: false,
    canGoBack: false,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.RV_DrinkingAlcohol]: {
    step: true,
    stepTitleIsNotPHI: true,
    canGoBack: true,
    label: 'How do you feel about your relationship with drinking alcohol?',
    type: QuestionTypes.One,
    options: alcoholUseOptions,
    canRewrite: true,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.RV_UseOfDrugs]: {
    step: true,
    stepTitleIsNotPHI: true,
    canGoBack: true,
    type: QuestionTypes.One,
    options: drugUseOptions,
    canRewrite: true,
    label: 'Do you use drugs?',
    secondaryText:
      "This includes medical or recreational marijuana, any substances you haven't been prescribed by a qualified medical professional, or any medications you've been prescribed but aren't using according to your doctor's instructions. \nWe'll ask you about medication you're taking as prescribed later.",
    assessmentSection: AssessmentSections.Clinical,
  },
};

const medicalInfoConfig: MetaConfig = {
  [Screens.MI_InsuranceCombo]: {
    step: true,
    label: 'We take insurance',
    subLabel:
      "We're in-network with major insurers, and we only see patients whose insurance we accept.",
    canRewrite: true,
    stepTitleIsNotPHI: true,
    canGoBack: true,
  },
  [Screens.MI_InsuranceForm]: {
    step: true,
    label: 'We take insurance',
    subLabel:
      "We're in-network with major insurers, and we only see patients whose insurance we accept.",
    canRewrite: true,
    stepTitleIsNotPHI: true,
    subTitle: 'Medical Information',
    canGoBack: true,
  },
  [Screens.NotInNetwork]: {
    stepTitleIsNotPHI: false,
    canGoBack: true,
  },
  [Screens.InNetwork]: {
    stepTitleIsNotPHI: false,
    canGoBack: true,
  },
  [Screens.MedicalInfoIntro]: {
    step: true,
    stepTitleIsNotPHI: true,
    canGoBack: true,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.MI_InsuranceId]: {
    step: true,
    label: 'What’s your insurance member ID?',
    canRewrite: true,
    stepTitleIsNotPHI: true,
    subTitle: 'Medical Information',
    canGoBack: true,
  },
  [Screens.MI_PrimaryPolicyHolder]: {
    label: 'Who is the policyholder?',
    canRewrite: true,
    stepTitleIsNotPHI: true,
    canGoBack: true,
    step: true,
  },
  [Screens.MI_MedicationPharmacyCombo]: {
    step: true,
    stepTitleIsNotPHI: true,
    label: 'Are you currently taking any prescription medication?',
    type: QuestionTypes.One,
    options: ['Yes', 'No'],
    canRewrite: true,
    canGoBack: true,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.MI_EmergencyContact]: {
    step: true,
    canGoBack: true,
    label: 'Who is your emergency contact?',
    canRewrite: true,
    stepTitleIsNotPHI: true,
    assessmentSection: AssessmentSections.Clinical,
  },
  [Screens.MI_HaveAppointment]: {
    step: true,
    stepTitleIsNotPHI: true,
    canGoBack: true,
    label: 'Do you already have an initial appointment scheduled?',
    type: QuestionTypes.One,
    options: ['Yes', 'No'],
    canRewrite: true,
    assessmentSection: AssessmentSections.Clinical,
  },
};

const preferencesConfig: MetaConfig = {
  [Screens.Review]: {
    label: 'Review your responses',
    subLabel:
      'Please review your responses and make any edits before submitting your intake form.',
    canGoBack: true,
    subTitle: 'Review',
    review: true,
    stepTitleIsNotPHI: true,
    assessmentSection: AssessmentSections.Clinical,
  },
};

const matchingConfig: MetaConfig = {
  [Screens.MatchLoading]: {
    stepTitleIsNotPHI: true,
    canGoBack: false,
    label: "We're matching you with doctors who meet your specific needs",
    assessmentSection: AssessmentSections.Booking,
  },
  [Screens.List]: {
    stepTitleIsNotPHI: true,
    canGoBack: false,
    assessmentSection: AssessmentSections.Booking,
  },
  [Screens.AppointmentConfirmation]: {
    stepTitleIsNotPHI: false,
    canGoBack: false,
    assessmentSection: AssessmentSections.Booking,
  },
  [Screens.NoMatch]: {
    label: 'Let’s help you find the right psychiatrist',
    stepTitleIsNotPHI: true,
    canGoBack: true,
    assessmentSection: AssessmentSections.Booking,
  },
};

const finishConfig: MetaConfig = {
  [Screens.Finish]: {
    label: 'Let’s help you find the right psychiatrist',
    stepTitleIsNotPHI: false,
    canGoBack: true,
    assessmentSection: AssessmentSections.Booking,
  },
};

/* addSectionToConfig is only done to ensure Freshpaint events match the previous state machine's events */
const addSectionToConfig = (sectionConfig: MetaConfig, sectionName: string) => {
  Object.keys(sectionConfig).forEach(screen => {
    sectionConfig[screen].section = sectionName;
  });
};
addSectionToConfig(authConfig, 'verifyEmail');
addSectionToConfig(demographicsConfig, 'demographics');
addSectionToConfig(reasonVisitConfig, 'reasonVisit');
addSectionToConfig(medicalInfoConfig, 'medicalInfo');
addSectionToConfig(preferencesConfig, 'preferences');
addSectionToConfig(matchingConfig, 'matching');

const metaScreenConfig: MetaConfig = {
  ...introductionConfig,
  ...authConfig,
  ...demographicsConfig,
  ...reasonVisitConfig,
  ...medicalInfoConfig,
  ...preferencesConfig,
  ...matchingConfig,
  ...finishConfig,
};

export const numberOfQuestions = Object.values(metaScreenConfig).filter(
  screen => 'step' in screen
).length;

export default metaScreenConfig;
